/**
 * Построение навигации из ответа /v1/platform/me.
 *
 * Чистые функции (без React) — легко тестировать и переиспользовать.
 * Generic SaaS: список модулей приходит от бэкенда, здесь только маппинг
 * machine-key → человекочитаемый label и нормализация структуры.
 */
import type { ModuleView } from '../api/platform-me.js';

/** Один пункт сайдбара. */
export interface NavItem {
  /** Машинный ключ модуля (для key в React). */
  key: string;
  /** Человекочитаемый label (русский). */
  label: string;
  /** Имя Phosphor-иконки. */
  icon: string;
  /** Роут внутри SPA. */
  path: string;
  /** Доступные действия/подразделы внутри модуля. */
  services: string[];
}

/**
 * Словарь label'ов по ключу модуля. Если ключ неизвестен — генерируем
 * читаемый fallback из самого ключа (capitalize + замена разделителей).
 */
const MODULE_LABELS: Readonly<Record<string, string>> = {
  dashboard: 'Главная',
  stays: 'Проживания',
  rooms: 'Номера',
  staff: 'Персонал',
  schedules: 'Графики',
  scheduling: 'Графики',
  requests: 'Заявки',
  services: 'Услуги',
  'service-catalog': 'Услуги',
  service_catalog: 'Услуги',
  catalog: 'Услуги',
  events: 'Анонсы',
  billing: 'Биллинг',
  vendors: 'Поставщики',
  suppliers: 'Поставщики',
  reviews: 'Отзывы',
  inventory: 'Склад',
  sla: 'Контроль сроков',
  'sla-monitor': 'Контроль сроков',
  reservations: 'Бронирования',
  profile: 'Профиль',
  subscriptions: 'Подписки',
};

/** Фоллбэк-label из ключа: `room-types` → `Room types`. */
export function humanizeKey(key: string): string {
  const cleaned = key.replace(/[_-]+/g, ' ').trim();
  if (cleaned.length === 0) return key;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/** Возвращает label модуля по ключу. */
export function labelForModule(key: string): string {
  return MODULE_LABELS[key] ?? humanizeKey(key);
}

/**
 * Строит список пунктов навигации из modules[].
 *
 * Фильтрует невалидные записи (без key/path), сохраняет порядок бэкенда.
 */
export function buildNavItems(modules: readonly ModuleView[]): NavItem[] {
  const items: NavItem[] = [];
  for (const m of modules) {
    if (!m || typeof m.key !== 'string' || m.key.length === 0) continue;
    if (typeof m.path !== 'string' || m.path.length === 0) continue;
    items.push({
      key: m.key,
      label: labelForModule(m.key),
      icon: typeof m.icon === 'string' ? m.icon : 'Circle',
      path: m.path,
      services: Array.isArray(m.services) ? m.services : [],
    });
  }
  return items;
}
